// The contract form's type picker, people list and extra terms. Terms appear only once added:
// the type suggests the usual ones, and a search box finds any of the catalogue's terms.
import { useId, useMemo, useState } from "react";
import { CATEGORIES, customKey, fieldFor, fieldKind, fieldLabel, groupLabel, searchFields, searchTypes, typeFor, typeRole, type ContractType } from "../../shared/catalog";
import { Button, Select, TextArea, TextInput, cx, focusRing } from "./ui";

export interface PartyRow {
  name: string;
  role: string;
  email: string;
  phone: string;
}

export interface DetailRow {
  key: string;
  value: string;
}

const chip = (active = false) =>
  cx(
    "rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors",
    focusRing,
    active ? "border-accent bg-accent-soft text-accent-soft-fg" : "border-line-strong bg-surface text-muted hover:border-accent hover:text-fg",
  );

// --- type ---------------------------------------------------------------------------

/** Searchable list of contract types, grouped by parent category. */
export function TypePicker({ value, onChange, suggested }: { value: string; onChange: (id: string) => void; suggested?: string | null }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const listId = useId();
  const current = typeFor(value);
  const results = useMemo(() => searchTypes(query), [query]);
  const grouped = useMemo(() => {
    // With a query, keep the best-first order in one list; without, group by category.
    if (query.trim()) return [{ id: "results", label: `${results.length} match${results.length === 1 ? "" : "es"}`, types: results }];
    return CATEGORIES.map((c) => ({ id: c.id, label: c.label, types: results.filter((t) => t.category === c.id) })).filter((g) => g.types.length);
  }, [query, results]);

  const choose = (t: ContractType | null) => {
    onChange(t?.id ?? "");
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" aria-expanded={open} aria-controls={listId} onClick={() => setOpen((o) => !o)}>
          {current ? current.label : "Choose a type"}
        </Button>
        {current && <span className="text-xs text-faint">{CATEGORIES.find((c) => c.id === current.category)?.label}</span>}
        {current && (
          <button type="button" className={cx("text-xs font-medium text-muted underline underline-offset-2 hover:text-fg", focusRing)} onClick={() => choose(null)}>
            Clear
          </button>
        )}
        {!current && suggested && typeFor(suggested) && (
          <button type="button" className={chip()} onClick={() => choose(typeFor(suggested)!)}>
            Suggested: {typeFor(suggested)!.label}
          </button>
        )}
      </div>
      {open && (
        <div id={listId} className="rounded-xl border border-line-strong bg-surface p-3 shadow-sm">
          <TextInput
            type="search"
            aria-label="Search contract types"
            placeholder="Search: lease, NDA, loan, lessee…"
            value={query}
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (results[0]) choose(results[0]);
              }
              if (e.key === "Escape") setOpen(false);
            }}
          />
          <div className="mt-2 max-h-80 space-y-3 overflow-y-auto" role="listbox" aria-label="Contract types">
            {grouped.length === 0 && <p className="px-1 py-2 text-sm text-faint">No type matches. Pick “Other”.</p>}
            {grouped.map((g) => (
              <div key={g.id} role="group" aria-label={g.label}>
                <div className="px-1 pb-1 text-xs font-semibold tracking-wide text-faint uppercase">{g.label}</div>
                <div className="flex flex-wrap gap-1.5">
                  {g.types.map((t) => (
                    <button key={t.id} type="button" role="option" aria-selected={t.id === value} className={chip(t.id === value)} onClick={() => choose(t)}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// --- people -------------------------------------------------------------------------

export function PartiesEditor({
  rows,
  onChange,
  type,
  found = [],
}: {
  rows: PartyRow[];
  onChange: (rows: PartyRow[]) => void;
  type: string;
  /** People the on-device reading found in the document, offered with one click. */
  found?: readonly { name: string; role?: string | null }[];
}) {
  const roles = typeFor(type)?.roles ?? [];
  const rolesId = useId();
  const update = (i: number, patch: Partial<PartyRow>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const add = (role = "") => onChange([...rows, { name: "", role, email: "", phone: "" }]);
  const missingRoles = roles.filter((r) => !rows.some((p) => p.role === r));
  // In the type's own words: a document's "Tenant" is a rent agreement's "Lessee (tenant)".
  const notAdded = found
    .filter((f) => !rows.some((p) => p.name.trim().toLowerCase() === f.name.toLowerCase()))
    .map((f) => ({ name: f.name, role: typeRole(type, f.role) ?? f.role ?? null }));

  return (
    <div className="space-y-3">
      <datalist id={rolesId}>
        {roles.map((r) => (
          <option key={r} value={r} />
        ))}
      </datalist>
      {rows.map((p, i) => (
        <fieldset key={i} className="grid gap-2 rounded-lg border border-line p-3 sm:grid-cols-[1fr_1.4fr_auto]" aria-label={`Person ${i + 1}`}>
          <TextInput aria-label={`Role of person ${i + 1}`} list={rolesId} placeholder="Role, e.g. Lessee" value={p.role} onChange={(e) => update(i, { role: e.target.value })} />
          <TextInput aria-label={`Name of person ${i + 1}`} placeholder="Name of person or company" value={p.name} onChange={(e) => update(i, { name: e.target.value })} />
          <Button variant="ghost" size="sm" aria-label={`Remove person ${i + 1}`} onClick={() => onChange(rows.filter((_, j) => j !== i))}>
            Remove
          </Button>
          <TextInput aria-label={`Email of person ${i + 1}`} type="email" placeholder="Email (optional)" value={p.email} onChange={(e) => update(i, { email: e.target.value })} />
          <TextInput aria-label={`Phone of person ${i + 1}`} type="tel" placeholder="Phone (optional)" value={p.phone} onChange={(e) => update(i, { phone: e.target.value })} />
        </fieldset>
      ))}
      {notAdded.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="People found in the document">
          <span className="text-xs text-faint">In the document:</span>
          {notAdded.map((f) => (
            <button key={f.name} type="button" className={chip()} onClick={() => onChange([...rows, { name: f.name, role: f.role ?? "", email: "", phone: "" }])}>
              + {f.name}
              {f.role && <span className="font-normal opacity-75"> · {f.role}</span>}
            </button>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        {missingRoles.map((r) => (
          <button key={r} type="button" className={chip()} onClick={() => add(r)}>
            + {r}
          </button>
        ))}
        <Button variant="secondary" size="sm" onClick={() => add()}>
          Add a person
        </Button>
      </div>
    </div>
  );
}

// --- terms --------------------------------------------------------------------------

function TermInput({ row, currency, onChange, invalid }: { row: DetailRow; currency: string; onChange: (v: string) => void; invalid?: boolean }) {
  const field = fieldFor(row.key);
  const label = fieldLabel(row.key);
  const common = { "aria-label": label, invalid, value: row.value };
  switch (fieldKind(row.key)) {
    case "longtext":
      return <TextArea {...common} rows={3} onChange={(e) => onChange(e.target.value)} />;
    case "date":
      return <TextInput {...common} type="date" min="1900-01-01" max="2200-12-31" onChange={(e) => onChange(e.target.value)} />;
    case "yesno":
      return (
        <Select {...common} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </Select>
      );
    case "select":
      return (
        <Select {...common} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {field!.options!.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </Select>
      );
    case "money":
      return (
        <div className="flex items-center gap-2">
          <TextInput {...common} inputMode="decimal" placeholder="Amount" onChange={(e) => onChange(e.target.value)} />
          <span className="text-xs text-faint">{currency || "—"}</span>
        </div>
      );
    case "percent":
      return (
        <div className="flex items-center gap-2">
          <TextInput {...common} inputMode="decimal" placeholder="e.g. 5" onChange={(e) => onChange(e.target.value)} />
          <span className="text-xs text-faint">%</span>
        </div>
      );
    case "number":
      return <TextInput {...common} inputMode="decimal" onChange={(e) => onChange(e.target.value)} />;
    default:
      return <TextInput {...common} onChange={(e) => onChange(e.target.value)} />;
  }
}

export function TermsEditor({
  rows,
  onChange,
  type,
  currency,
  error,
  aiKeys,
  found,
}: {
  rows: DetailRow[];
  onChange: (rows: DetailRow[]) => void;
  type: string;
  currency: string;
  error?: string;
  /** Terms filled in by AI, marked until the user edits them. */
  aiKeys?: ReadonlySet<string>;
  /** Terms read from the document by the rules, with the passage each came from (shown while unchanged). */
  found?: Readonly<Record<string, { value: string; evidence: string }>>;
}) {
  const [query, setQuery] = useState("");
  const have = new Set(rows.map((r) => r.key));
  const suggested = (typeFor(type)?.fields ?? []).filter((k) => !have.has(k));
  const results = useMemo(() => (query.trim() ? searchFields(query, have).slice(0, 12) : []), [query, rows]); // eslint-disable-line react-hooks/exhaustive-deps
  const add = (key: string) => {
    if (have.has(key)) return;
    onChange([...rows, { key, value: "" }]);
    setQuery("");
  };
  const custom = query.trim() ? customKey(query) : null;

  return (
    <div className="space-y-4">
      {rows.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {rows.map((r, i) => (
            <div key={r.key} className={cx("space-y-1.5", fieldKind(r.key) === "longtext" && "sm:col-span-2")}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-fg">
                  {fieldLabel(r.key)}
                  {aiKeys?.has(r.key) && <span className="ml-1.5 rounded bg-accent-soft px-1.5 py-0.5 text-[10px] font-semibold text-accent-soft-fg uppercase">AI</span>}
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${fieldLabel(r.key)}`}
                  className={cx("text-xs font-medium text-muted hover:text-bad", focusRing)}
                  onClick={() => onChange(rows.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </div>
              <TermInput row={r} currency={currency} onChange={(value) => onChange(rows.map((x, j) => (j === i ? { ...x, value } : x)))} />
              {found?.[r.key] && found[r.key]!.value === r.value && !aiKeys?.has(r.key) && (
                <p className="text-xs leading-relaxed text-faint" title={found[r.key]!.evidence}>
                  From the document: “{found[r.key]!.evidence.slice(0, 120)}”
                </p>
              )}
            </div>
          ))}
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm text-bad">
          {error}
        </p>
      )}
      {suggested.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Suggested terms">
          <span className="text-xs text-faint">Usual for {typeFor(type)!.label}:</span>
          {suggested.map((k) => (
            <button key={k} type="button" className={chip()} onClick={() => add(k)}>
              + {fieldLabel(k)}
            </button>
          ))}
        </div>
      )}
      <div className="space-y-2">
        <TextInput
          type="search"
          aria-label="Search terms to add"
          placeholder="Add a term: deposit, lock-in, governing law, GST…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            if (results[0]) add(results[0].key);
            else if (custom) add(custom);
          }}
        />
        {query.trim() && (
          <ul className="max-h-64 divide-y divide-line overflow-y-auto rounded-lg border border-line" aria-label="Matching terms">
            {results.map((f) => (
              <li key={f.key}>
                <button type="button" className={cx("flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-subtle", focusRing)} onClick={() => add(f.key)}>
                  <span>{f.label}</span>
                  <span className="text-xs text-faint">{groupLabel(f.group)}</span>
                </button>
              </li>
            ))}
            {custom && !have.has(custom) && (
              <li>
                <button type="button" className={cx("w-full px-3 py-2 text-left text-sm font-medium text-accent-fg hover:bg-subtle", focusRing)} onClick={() => add(custom)}>
                  Add custom term “{query.trim()}”
                </button>
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}
